import { Moment } from "moment";
import monday from "./monday";
import debug from "../debug";
import {
    ActivityLog,
    ActivityLogUpdateColumnValueData,
    Board,
    Column,
    ColumnSettingsStr,
    Item,
    Labels,
    LabelStyle
} from "../types";
import { getLabelStyle } from "../constants";
import { getTransparentRGB } from "../tools";

const log = debug("data");

export type DataError = { error_msg: string; error_extra: any; error_err: Error };

export type BoardData = { boards: Board[]; allFetchedColumns: Set<string> };

export type BoardDataResult = BoardData | DataError;

// https://developer.monday.com/api-reference/docs/errors
export function handleMondayApiError(result: any): void {
    if (
        Object.prototype.hasOwnProperty.call(result, "error_message") ||
        Object.prototype.hasOwnProperty.call(result, "error_code") ||
        Object.prototype.hasOwnProperty.call(result, "errors")
    ) {
        const messages: string[] = result.errors?.map((error: any) => error.message) || [];
        if (result.error_code) {
            messages.push(result.error_code);
        }
        if (result.error_message) {
            messages.push(result.error_message);
        }
        throw new Error(`Error occurred while making request to Monday API: ${messages.join(", ")}`);
    }
}

async function callMondayApi(query: string, variables: any): Promise<{ data?: any; error?: DataError }> {
    try {
        const response = await monday.api(query, { variables });
        handleMondayApiError(response);

        if (response && response.data) {
            // const complexity = response.data.complexity;
            // log(`query complexity: ${complexity.query}, after: ${complexity.after}`);
            return { data: response.data };
        } else {
            throw new Error("Monday.api didn't return data: " + JSON.stringify(response));
        }
    } catch (err) {
        const varsStr = JSON.stringify(variables);
        console.error(`API call error for query "${query}", vars: ${varsStr}`, err);
        return { error: { error_msg: "API call error", error_extra: { query, variables: varsStr }, error_err: err as Error } };
    }
}

function getColumnsUsedInFormulas(columns: Column[]): Set<string> {
    const result: Set<string> = new Set();
    const regexp = /{(.+?)}/g;
    columns.forEach((column) => {
        result.add(column.id);
        if (column.type === "formula") {
            const settingsStr = JSON.parse(column.settings_str);
            const formula: string = settingsStr.formula;
            const matches = formula.matchAll(regexp);
            for (const match of matches) {
                result.add(match[1]);
            }
        }
    });
    return result;
}

export async function getBoardData(
    boardId: string,
    columnIds: string[],
    itemIds: number[],
    from: Moment,
    withActivityLogs: boolean,
    progressCallback?: (percentage: number) => void
): Promise<BoardDataResult> {
    const boardInfoQuery = `query ($boardIds: [ID!], $columnIds: [String]) {
        boards(ids: $boardIds) {
            id
            name
            columns(ids: $columnIds) {
                id
                type
                title
                settings_str
            }
        }
    }`;

    const rowDataQuery = `query ($boardIds: [ID!], $columnIds: [String!], $itemIds: [ID!]${
        withActivityLogs ? ", $from: ISO8601DateTime" : ""
    }) {
        boards(ids: $boardIds) {
            ${
                withActivityLogs
                    ? `
            activity_logs(limit: 10000, column_ids: $columnIds, item_ids: $itemIds, from: $from) {
                created_at
                data
                event
            }
            `
                    : ""
            }
            items_page(limit: 500, query_params: { ids:$itemIds }) {
                cursor
                items {
                    id
                    state
                    created_at
                    name
                    column_values(ids: $columnIds) {
                        id
                        type
                        value
                        text
                        ...on StatusValue {
                            label
                            label_style {
                                color
                                border
                            }
                        }
                    }
                }
            }
        }
    }`;

    const PAGE_SIZE = withActivityLogs ? 50 : 100;
    const result: { boards: Board[]; allFetchedColumns: Set<string> } = { boards: [], allFetchedColumns: new Set() };

    const response = await callMondayApi(boardInfoQuery, {
        boardIds: [boardId],
        columnIds
    });
    if (response.error) {
        return response.error;
    }
    result.boards = response.data.boards;
    result.boards[0].items = [];
    result.boards[0].activity_logs = [];

    const columnsToFetch = getColumnsUsedInFormulas(result.boards[0].columns);
    result.allFetchedColumns = columnsToFetch;

    let itemIdsOffset = 0;
    while (itemIdsOffset < itemIds.length) {
        const response = await callMondayApi(rowDataQuery, {
            boardIds: [boardId],
            columnIds: [...columnsToFetch],
            itemIds: itemIds.slice(itemIdsOffset, itemIdsOffset + PAGE_SIZE),
            from: from.format()
        });
        if (response.error) {
            return response.error;
        }
        // merge data into result
        result.boards[0].items = result.boards[0].items.concat(response.data.boards[0].items_page.items);
        if (result.boards[0].activity_logs && response.data.boards[0].activity_logs) {
            result.boards[0].activity_logs = result.boards[0].activity_logs.concat(
                response.data.boards[0].activity_logs
            );
        }
        itemIdsOffset += PAGE_SIZE;
        if (progressCallback) {
            progressCallback((itemIdsOffset / itemIds.length) * 100);
        }
    }

    const logs = result.boards[0].activity_logs;
    log(`got ${logs?.length} activity logs`);
    if (logs) {
        // sort logs in desc order
        if (itemIds.length > PAGE_SIZE - 1) {
            logs.sort((a1, a2) => a2.created_at - a1.created_at);
        }
        // parse data for activity_logs - it's used more than once
        logs.forEach((log) => {
            log.dataJson = JSON.parse(log.data!);
            delete log.data;
        });
    }

    const items = result.boards[0].items;
    log(`got ${items.length} items`);
    items.forEach((item) => {
        item.column_values.forEach((value) => {
            if (value.value !== null) {
                value.valueJson = JSON.parse(value.value!);
                delete value.value;
            }
        });
    });

    return result;
}

const EMPTY_NAME = "[empty]";
const DELETED_NAME = "[deleted]";

export function sanitiseColorColumn(colorColumn: Column, activityLogs: ActivityLog[] | null, items: Item[]): Labels {
    const settings: ColumnSettingsStr = JSON.parse(colorColumn.settings_str);

    log("status column settings", colorColumn.title, JSON.stringify(settings));

    let orderedIds: string[] = [];
    let order: { [labelId: string]: number } = {};

    let result: Labels = {
        names: { ...settings.labels },
        order,
        orderedIds,
        labelsColors: settings.labels_colors
    };

    try {
        // determine order from settings or use random
        if (settings.labels_positions_v2) {
            orderedIds = Object.keys(settings.labels_positions_v2)
                .filter((labelId) => settings.labels[labelId] !== undefined)
                .sort((l1, l2) => settings.labels_positions_v2![l1] - settings.labels_positions_v2![l2]);

            order = settings.labels_positions_v2;
            Object.keys(order).forEach((labelId) => {
                order[labelId] = parseInt(order[labelId] + "");
            });
        } else {
            orderedIds = Object.keys(settings.labels);
            orderedIds.forEach((labelId, idx) => (order[labelId] = idx));
        }

        // some labels may not have names, so add empty ones
        orderedIds.forEach((labelId) => {
            if (!settings.labels[labelId]) {
                settings.labels[labelId] = EMPTY_NAME;
            }
        });

        // get colors from settings or generate them sequentially from constants
        const labelsColors: { [id: string]: LabelStyle } = settings.labels_colors || {};
        orderedIds.forEach((id, idx) => {
            if (labelsColors[id] === undefined) {
                labelsColors[id] = getLabelStyle(idx);
            }
            labelsColors[id].colorInactive = getTransparentRGB(labelsColors[id].color, "8", "80");
        });

        // get missing labels from current items
        const names = { ...settings.labels };
        items.forEach((item) =>
            item.column_values.forEach((value) => {
                if (value.id === colorColumn.id && value.valueJson !== undefined) {
                    if (!names[value.valueJson.index]) {
                        console.log(`adding missing label: ${value.valueJson.index}`);
                        const i = value.valueJson.index;
                        // const additionalInfo = { label: value.label, color: value.label_style?.color };
                        names[i] = value?.label || EMPTY_NAME;
                        order[i] = orderedIds.length;
                        if (value.label_style?.color) {
                            labelsColors[i] = {
                                color: value.label_style?.color,
                                border: value.label_style?.color,
                                var_name: `generated-${i}`
                            };
                        } else {
                            labelsColors[i] = getLabelStyle(orderedIds.length);
                        }
                        labelsColors[i].colorInactive = getTransparentRGB(labelsColors[i].color, "8", "80");
                        orderedIds.push(i + "");
                    }
                }
            })
        );

        // get missing labels from activity logs (in case some labels got deleted)
        activityLogs?.forEach((log) => {
            if (log.event === "update_column_value") {
                const data = log.dataJson as ActivityLogUpdateColumnValueData;
                if (data.column_id === colorColumn.id) {
                    const label = data.previous_value?.label;
                    if (label && !names[label.index]) {
                        // unknown deleted label, add at the end
                        console.log(`adding deleted label: ${label.index}`);
                        names[label.index] = label.text || DELETED_NAME;
                        order[label.index] = orderedIds.length;
                        labelsColors[label.index] = label.style;
                        labelsColors[label.index].colorInactive = getTransparentRGB(
                            labelsColors[label.index].color,
                            "8",
                            "80"
                        );
                        orderedIds.push(label.index + "");
                    }
                }
            }
        });

        result = {
            names,
            order,
            orderedIds,
            labelsColors,
            done: settings.done_colors
        };
    } catch (err) {
        console.error(err);
        window.Rollbar?.error("sanitiseColorColumn", { settings: JSON.stringify(settings) }, err);
    }

    log("processed", JSON.stringify(result));

    return result;
}
